import React from 'react';
import { useQuery } from 'react-query';
import { Avatar, Box, Chip, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { useAuth } from '../components/auth/AuthProvider';
import { useApiClient } from '../utils/apiClient';

interface BackendProfile {
  id: string;
  email: string;
  name: string;
  roles: string[];
  department?: string;
  job_title?: string;
  avatar_url?: string;
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const api = useApiClient();

  const { data, isLoading, isFetching, isError } = useQuery<BackendProfile>(
    ['profile'],
    () => api.get<BackendProfile>('/auth/profile'),
    { staleTime: 60_000, refetchOnWindowFocus: false }
  );

  const merged = {
    id: user?.id || data?.id || '',
    name: data?.name || user?.name || '',
    email: data?.email || user?.email || '',
    roles: data?.roles || user?.roles || [],
    department: data?.department ?? user?.department,
    jobTitle: (data?.job_title as string | undefined) ?? user?.jobTitle,
    avatar: data?.avatar_url ?? user?.avatar,
  };

  const initials = (name: string) =>
    (name || 'U')
      .split(' ')
      .map((s) => s[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h4" fontWeight={700}>My Profile</Typography>
        <Typography variant="body1" color="text.secondary">Your account information and access</Typography>
      </Box>

      <Paper className="p-6 shadow-card">
        {(isLoading || isFetching) && (
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading profile…</Typography>
          </Box>
        )}
        {isError && (
          <Box mb={2}>
            <Typography variant="body2" color="error.main">Failed to load profile from server. Showing cached info.</Typography>
          </Box>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Avatar src={merged.avatar} sx={{ width: 80, height: 80, bgcolor: '#1e40af' }}>
            {!merged.avatar && initials(merged.name)}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h6" fontWeight={700}>{merged.name}</Typography>
            <Typography variant="body2" color="text.secondary">{merged.email}</Typography>
            <Box mt={1} display="flex" gap={1} flexWrap="wrap">
              {(merged.roles || []).map((r) => (
                <Chip key={r} size="small" label={r} color={r === 'admin' || r === 'super_admin' ? 'secondary' : 'default'} />
              ))}
              {(!merged.roles || merged.roles.length === 0) && (
                <Chip size="small" label="employee" />
              )}
            </Box>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
          <Box minWidth={220}>
            <Typography variant="overline" color="text.secondary">Department</Typography>
            <Typography variant="body1">{merged.department || '-'}</Typography>
          </Box>
          <Box minWidth={220}>
            <Typography variant="overline" color="text.secondary">Job Title</Typography>
            <Typography variant="body1">{merged.jobTitle || '-'}</Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper className="p-6 shadow-card">
        <Typography variant="h6" fontWeight={700} gutterBottom>Security</Typography>
        <Typography variant="body2" color="text.secondary">If anything looks wrong with your profile or access, contact an administrator.</Typography>
      </Paper>
    </Stack>
  );
};

export default Profile;
